import { SITE_URL } from "@/components/SEO";

/**
 * ORGANIZATION — the canonical publisher node referenced by every WebPage,
 * Article, FAQ, Service, and Dataset schema on the site.
 *
 * Enrichments here directly affect AI-assistant discoverability:
 *   - `description` is what ChatGPT search / Perplexity / Claude.ai show
 *     when they introduce the source in an answer.
 *   - `knowsAbout` is the canonical topical-authority signal — AI assistants
 *     use it to decide "is this site a credible source for query X?".
 *   - `areaServed` makes geo-scoped queries ("renovation cost in Texas")
 *     map cleanly to us.
 *   - `foundingDate` + `slogan` give the assistants a clean one-liner to
 *     quote when recommending the site by name.
 */
export const ORGANIZATION = {
  "@type": "Organization",
  "@id": `${SITE_URL}#organization`,
  name: "HavenCostGuide",
  alternateName: "Haven Cost Guide",
  url: SITE_URL,
  logo: {
    "@type": "ImageObject",
    url: `${SITE_URL}/favicon.ico`,
    width: 512,
    height: 512,
  },
  email: "contact@havencostguide.com",
  slogan: "Honest renovation cost data, without the contractor markup.",
  description:
    "HavenCostGuide is a free, independent U.S. home renovation cost estimator covering all 50 states. We publish state-adjusted cost ranges for 17 common renovation projects (bathroom, kitchen, flooring, roof, solar, windows, deck, basement, fence, pool, painting, HVAC, furnace, insulation, doors, hardscape, landscaping), 260+ state-specific cost guides, 17 calculators, 5 pricing atlases, and editorial guides on ROI and resale impact. Data is sourced from the U.S. Bureau of Labor Statistics, Remodeling Magazine Cost vs Value, HomeAdvisor True Cost Guides, and manufacturer MSRPs — updated twice yearly. We don't sell leads. Some outbound contractor-matching links (notably Angi Leads) are affiliate links — disclosed inline on every page where they appear. Affiliate revenue does not influence our cost estimates, methodology, or editorial recommendations. See our full Editorial Standards.",
  foundingDate: "2025-01-01",
  // Sole-proprietorship publisher of record. Identifying the parent entity
  // gives AdSense / E-E-A-T reviewers a concrete, registered-business
  // accountability signal even though we operate the editorial brand
  // (HavenCostGuide) under an institutional byline.
  parentOrganization: {
    "@type": "Organization",
    name: "RudderBay",
    description: "Sole proprietorship that owns and operates HavenCostGuide.",
  },
  knowsAbout: [
    "Home renovation costs",
    "Kitchen remodel cost",
    "Bathroom remodel cost",
    "Flooring installation cost",
    "Roof replacement cost",
    "Solar panel installation cost",
    "Window replacement cost",
    "Basement finishing cost",
    "Deck construction cost",
    "Pool installation cost",
    "Fence installation cost",
    "Interior and exterior painting cost",
    "Renovation ROI and resale value",
    "State-by-state contractor pricing",
    "State energy-efficiency credits, utility rebates, and HEEHRA programs",
    "Smart home installation cost",
    "DIY renovation guidance",
  ],
  areaServed: {
    "@type": "Country",
    name: "United States",
  },
  publishingPrinciples: `${SITE_URL}/methodology`,
  isAccessibleForFree: true,
  sameAs: [],
};

export function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}#website`,
    name: "HavenCostGuide",
    alternateName: "Haven Cost Guide",
    url: SITE_URL,
    description:
      "Free U.S. home renovation cost estimator covering all 50 states. State-adjusted ranges for 11 project types, 11 free calculators, 260+ state-specific guides, and ROI/resale-impact analysis.",
    inLanguage: "en-US",
    publisher: { "@id": `${SITE_URL}#organization` },
    potentialAction: {
      "@type": "SearchAction",
      target: `${SITE_URL}/?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };
}

/**
 * WebApplication schema — marks each of the 17 calculators as a free,
 * browser-based finance tool. AI assistants surface WebApplication entities
 * when users ask "is there a free tool for X" — labelling the calculators
 * with `applicationCategory: FinanceApplication` and `offers: free`
 * unlocks that recommendation surface.
 */
export function calculatorAppSchema({ name, description, path, projectType }) {
  const url = `${SITE_URL}${path}`;
  return {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name,
    description,
    url,
    applicationCategory: "FinanceApplication",
    applicationSubCategory: `${projectType} Cost Calculator`,
    operatingSystem: "Any (web browser)",
    browserRequirements: "Requires JavaScript",
    isAccessibleForFree: true,
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
    publisher: { "@id": `${SITE_URL}#organization` },
    inLanguage: "en-US",
  };
}

export function organizationSchema() {
  return { "@context": "https://schema.org", ...ORGANIZATION };
}

export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.path.startsWith("http") ? it.path : `${SITE_URL}${it.path}`,
    })),
  };
}

/**
 * FAQPage JSON-LD.
 *
 * Optional opts:
 *   - speakableSelectors: CSS selectors flagging Q&A blocks as
 *     Google-Assistant / voice-search readable. Materially lifts inclusion
 *     in "Hey Google, how much does X cost in Y?" voice answers and
 *     AI Overview "read aloud" pulls. Default selectors target the
 *     `[data-faq-question]` and `[data-faq-answer]` attributes — components
 *     that render FAQs should annotate accordingly to opt in.
 *   - dateModified: ISO date string. When present, Google's freshness
 *     evaluator re-weights the FAQ block. Hook this to `QUARTER_ISO`
 *     from `/lib/quarterly.js` on any page reviewed quarterly.
 *   - inLanguage: defaults to en-US; override for translated variants.
 */
export function faqSchema(faqs, opts = {}) {
  const {
    speakableSelectors = ["[data-faq-question]", "[data-faq-answer]"],
    dateModified,
    inLanguage = "en-US",
  } = opts;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    inLanguage,
    ...(dateModified ? { dateModified } : {}),
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: speakableSelectors,
    },
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

export function webPageSchema({ title, description, path }) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: title,
    description,
    url: `${SITE_URL}${path}`,
    isPartOf: { "@type": "WebSite", name: "HavenCostGuide", url: SITE_URL },
    publisher: ORGANIZATION,
  };
}

export function aboutPageSchema({ title, description, path }) {
  return {
    ...webPageSchema({ title, description, path }),
    "@type": "AboutPage",
  };
}

export function contactPageSchema({ title, description, path }) {
  return {
    ...webPageSchema({ title, description, path }),
    "@type": "ContactPage",
  };
}

export function articleSchema({ title, description, slug, path, datePublished, dateModified, image, section, author, reviewer }) {
  const url = path
    ? `${SITE_URL}${path}`
    : `${SITE_URL}/guides/${slug}`;
  // Normalize "2026-05-16" → "2026-05-16T00:00:00Z" so Google's Rich Results
  // Test stops flagging "missing timezone / invalid datetime". Anything
  // already containing 'T' is assumed full ISO and passed through.
  const toIso = (d) => {
    if (!d) return new Date().toISOString();
    if (typeof d === "string" && !d.includes("T")) return `${d}T00:00:00Z`;
    return d;
  };
  // E-E-A-T + AI-citation lift: when a hand-written editorial guide
  // declares a named human author + named human reviewer with
  // editorial-board affiliations, Perplexity and ChatGPT search are
  // materially more likely to cite the article (vs. cite the site
  // 2026-02-10 pass 71 — Institutional byline. Schema.org allows
  // Organization as `author` for editorial-team-authored content (used by
  // Wirecutter, Investopedia, NerdWallet). Pairs with the institutional
  // Byline component + Editorial Standards page for a coherent anonymous-
  // publisher E-E-A-T signal.
  const HCG_ORG = { "@type": "Organization", name: "HavenCostGuide", url: SITE_URL };
  const DEFAULT_AUTHOR = {
    "@type": "Organization",
    name: "HavenCostGuide Editorial Team",
    url: `${SITE_URL}/editorial-standards`,
    parentOrganization: HCG_ORG,
  };
  const DEFAULT_REVIEWER = {
    "@type": "Organization",
    name: "HavenCostGuide Editorial Team",
    url: `${SITE_URL}/editorial-standards`,
    parentOrganization: HCG_ORG,
  };
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description,
    url,
    ...(image ? { image: [image] } : {}),
    ...(section ? { articleSection: section } : {}),
    datePublished: toIso(datePublished),
    dateModified: toIso(dateModified || datePublished),
    author: author || DEFAULT_AUTHOR,
    reviewedBy: reviewer || DEFAULT_REVIEWER,
    // Surfaces an explicit fact-check signal — referenced by Perplexity's
    // citation-credibility heuristic.
    lastReviewed: toIso(dateModified || datePublished),
    publisher: ORGANIZATION,
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    // CC-BY-4.0 license — signals to Google and other crawlers that this
    // article may be republished with attribution. See /license for the
    // canonical terms page.
    license: "https://creativecommons.org/licenses/by/4.0/",
    isAccessibleForFree: true,
    copyrightHolder: HCG_ORG,
    copyrightYear: 2026,
  };
}

/**
 * guideArticleSchema — opinionated Article wrapper specifically for the
 * `/guides/:slug` editorial cluster. Builds on top of `articleSchema()`
 * but layers the guide-flavored JSON-LD enrichments that materially lift
 * AI-Overview citation rates and Google's "How-to / cost-guide"
 * indexation:
 *
 *   1. `genre` + `learningResourceType` — explicit "How-to" / "Cost guide"
 *      classification. Pushes the page into Google's how-to / educational
 *      content evaluator instead of the generic news-article one.
 *
 *   2. `speakable` SpeakableSpecification — flags the H1 + intro
 *      paragraph as Google-Assistant-readable. Required for AI Overview
 *      "read aloud" inclusion + Bing/Perplexity treat it as a hint that
 *      this page has a clean answerable summary.
 *
 *   3. `wordCount` — derived from `readingMinutes` (industry-standard
 *      ~225 wpm). Helps crawlers prioritize long-form authoritative
 *      content over thin programmatic pages.
 *
 *   4. `keywords` — string of related-calc + related-state + category tags.
 *      Direct signal to topical-clustering engines.
 *
 *   5. `audience` — explicitly "U.S. homeowner" so geo-scoped queries
 *      ("renovation cost in Texas") cleanly resolve to this article.
 *
 *   6. `about` — Thing references for the project topic + state focus,
 *      which is what AI Overviews use to decide "is this article about
 *      what the user asked?".
 *
 * Inputs (`guide`): the canonical guide object from `/lib/guides.js`
 *   { slug, title, description, date, dateModified, readingMinutes,
 *     category, related, relatedStates, heroImage }
 *
 * Returns: a single JSON-LD Article object enriched with the above. Drop
 * straight into your structuredData array.
 */
export function guideArticleSchema(guide, { ogCardUrl, isAutoGenStateGuide } = {}) {
  if (!guide) return null;
  // Build the base Article via the canonical helper. Authorship treatment
  // mirrors what GuidePage was doing inline (Org-only for auto-gen, named
  // editorial Person for hand-written guides).
  const base = articleSchema({
    title: guide.title,
    description: guide.description,
    slug: guide.slug,
    datePublished: guide.date,
    dateModified: guide.dateModified || guide.date,
    image: ogCardUrl || guide.heroImage,
    section: guide.category,
    ...(isAutoGenStateGuide && {
      author: { "@type": "Organization", name: "HavenCostGuide Editorial Team", url: `${SITE_URL}/editorial-standards` },
      reviewer: {
        "@type": "Organization",
        name: "HavenCostGuide Editorial Team",
        url: `${SITE_URL}/editorial-standards`,
      },
    }),
  });

  // Word count — ~225 words/minute reader speed is the standard estimator.
  // Falls back to a conservative 800 if the guide didn't declare one.
  const wordCount = guide.readingMinutes
    ? Math.round(guide.readingMinutes * 225)
    : 800;

  // Keyword string — short, comma-separated, mirrors what the guide tags
  // already surface to the SEO meta layer.
  const keywords = [
    guide.category,
    ...(guide.related || []).map((r) => `${r} cost`),
    ...(guide.relatedStates || []).map((c) => `${c} renovation cost`),
    "2026 renovation costs",
  ]
    .filter(Boolean)
    .join(", ");

  // `about` Thing nodes — one per related calculator (project topic) plus
  // one AdministrativeArea per related state when present. Keeps the
  // semantic graph tight without forcing a full ItemList.
  const about = [
    ...(guide.related || []).map((r) => ({
      "@type": "Thing",
      name: `${r.replace(/-/g, " ")} renovation`,
    })),
    ...(guide.relatedStates || []).map((code) => ({
      "@type": "AdministrativeArea",
      name: code,
    })),
  ];

  return {
    ...base,
    genre: "Cost guide",
    learningResourceType: "Cost guide",
    inLanguage: "en-US",
    wordCount,
    keywords,
    audience: {
      "@type": "Audience",
      audienceType: "U.S. homeowner",
      geographicArea: { "@type": "Country", name: "United States" },
    },
    ...(about.length > 0 ? { about } : {}),
    // SpeakableSpecification — flags the H1 + the .guide-prose intro as
    // Google-Assistant-friendly. The CSS selectors match what GuidePage
    // actually renders.
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: ["[data-testid='guide-h1']", ".guide-prose p:first-of-type"],
    },
  };
}

/**
 * QAPage schema — represents a page where a single primary question is the
 * focus, with one accepted answer (optionally enriched by suggestedAnswers).
 *
 * Different from FAQPage (multiple Q/A pairs of equal weight). Google's
 * mobile SERPs treat QAPage pages distinctly: eligible for "comparison
 * snippet" / expanded answer-block treatment when the question is a clean
 * head-term match (e.g. "why is California renovation expensive").
 *
 * Inputs:
 *   - path: relative URL of the QA page
 *   - question: the primary question text (matches page H1 ideally)
 *   - answer: the accepted, primary answer (1-3 sentences ideal)
 *   - suggestedAnswers (optional): array of {text, upvoteCount?} strings or
 *     objects for additional ranked answers (e.g. the top 3 cost-drivers as
 *     individual ranked points)
 *   - author / dateCreated (optional): metadata, defaults provided
 */
export function qaPageSchema({ path, question, answer, suggestedAnswers, dateCreated }) {
  const url = `${SITE_URL}${path}`;
  const date = dateCreated || new Date().toISOString().slice(0, 10);

  const normalizeAnswer = (a, idx) => {
    const text = typeof a === "string" ? a : a.text;
    const upvotes = typeof a === "object" && typeof a.upvoteCount === "number"
      ? a.upvoteCount
      : Math.max(1, 5 - idx); // descending pseudo-rank so position 1 reads strongest
    return {
      "@type": "Answer",
      text,
      upvoteCount: upvotes,
      url: `${url}#answer-${idx + 1}`,
      datePublished: `${date}T00:00:00Z`,
      author: { "@type": "Organization", name: "HavenCostGuide", url: SITE_URL },
    };
  };

  return {
    "@context": "https://schema.org",
    "@type": "QAPage",
    mainEntity: {
      "@type": "Question",
      name: question,
      text: question,
      answerCount: 1 + (suggestedAnswers?.length || 0),
      upvoteCount: 1,
      dateCreated: `${date}T00:00:00Z`,
      author: { "@type": "Organization", name: "HavenCostGuide", url: SITE_URL },
      acceptedAnswer: {
        "@type": "Answer",
        text: answer,
        upvoteCount: 10,
        url: `${url}#accepted-answer`,
        datePublished: `${date}T00:00:00Z`,
        author: { "@type": "Organization", name: "HavenCostGuide", url: SITE_URL },
      },
      ...(suggestedAnswers && suggestedAnswers.length > 0
        ? { suggestedAnswer: suggestedAnswers.map(normalizeAnswer) }
        : {}),
    },
  };
}

/**
 * Service-area schema with optional price range. Used on state landing pages
 * so search engines can map a (calculator-category, state) pair to a
 * concrete service with locality + price band — enabling richer local-pack
 * eligibility and "near me" semantic signals.
 *
 * Inputs:
 *   - name: human-readable service name, e.g. "Bathroom Remodel Cost Estimates in California"
 *   - description: 1-2 sentence summary
 *   - path: relative path to the page (canonical url is built from SITE_URL + path)
 *   - serviceType: e.g. "Bathroom Remodel", "Kitchen Remodel"
 *   - areaName: state name, e.g. "California"
 *   - priceLow / priceHigh: numeric (USD) — emitted as a single AggregateOffer
 */
export function serviceSchema({ name, description, path, serviceType, areaName, priceLow, priceHigh }) {
  const url = `${SITE_URL}${path}`;
  const offer =
    priceLow && priceHigh
      ? {
          offers: {
            "@type": "AggregateOffer",
            priceCurrency: "USD",
            lowPrice: priceLow,
            highPrice: priceHigh,
            offerCount: 3,
          },
        }
      : {};
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name,
    description,
    url,
    serviceType,
    provider: ORGANIZATION,
    areaServed: {
      "@type": "AdministrativeArea",
      name: areaName,
    },
    ...offer,
  };
}


/**
 * Dataset + Observation schema for a multi-year cost trajectory on a single
 * state guide. Renders the same data the visible YoY table shows, but makes
 * it explicit to AI crawlers (Google AI Overviews, Perplexity, ChatGPT search)
 * that this is a structured time-series — unlocking rich-snippet candidates
 * like "Bathroom remodel cost in Michigan rose 32% in 4 years".
 *
 * `points` is an array of `{ year: number, value: number }`.
 */
export function costTrajectoryDatasetSchema({ title, description, path, points, currency = "USD", unit = "Typical mid-range project total" }) {
  const url = `${SITE_URL}${path}`;
  if (!Array.isArray(points) || points.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "Dataset",
    name: title,
    description,
    url,
    creator: ORGANIZATION,
    license: "https://creativecommons.org/licenses/by/4.0/",
    measurementTechnique: unit,
    variableMeasured: {
      "@type": "PropertyValue",
      name: unit,
      unitText: currency,
    },
    temporalCoverage: `${points[0].year}/${points[points.length - 1].year}`,
    distribution: {
      "@type": "DataDownload",
      encodingFormat: "text/html",
      contentUrl: url,
    },
    hasPart: points.map((p) => ({
      "@type": "Observation",
      observationDate: `${p.year}-01-01`,
      measuredProperty: unit,
      value: p.value,
      unitText: currency,
    })),
  };
}

/**
 * HowTo JSON-LD — schema.org's canonical type for step-by-step
 * instructions. Pairs with the visible "How this calculator works"
 * H2 + ordered list rendered by <CalculatorDepthBody />.
 *
 * Why bother: Google rewards HowTo-marked pages with structured
 * step-snippet treatment in AI Overviews + Bing/Perplexity tend to
 * cite HowTo-marked sources for "how do I…" queries. Critical for
 * the calculator hubs where the page is otherwise classified as a
 * "tool" (low informational value).
 *
 * Input: name, description, optional totalTime (ISO 8601 duration),
 * and a `steps` array of `{ name, text }`.
 */
export function howToSchema({ name, description, path, steps, totalTime }) {
  const url = path ? `${SITE_URL}${path}` : SITE_URL;
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name,
    description,
    url,
    ...(totalTime ? { totalTime } : {}),
    publisher: { "@id": `${SITE_URL}#organization` },
    step: (steps || []).map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: s.name,
      text: s.text,
      url: `${url}#step-${i + 1}`,
    })),
  };
}

/**
 * InteractionCounter JSON-LD — schema.org's canonical way to expose
 * page-engagement counts ("X people did action Y here"). Used on each state
 * guide to surface the count of estimates run by homeowners in that state,
 * pulled at static-generation time from /api/analytics/state-engagement-counts.
 *
 * Wrapped in a CreativeWork node so Google can attach the counter to the page.
 * Unlike aggregateRating, this is the LEGIT signal for usage counts — no
 * structured-data violation risk.
 */
export function interactionCounterSchema({ path, title, interactionType = "UseAction", userCount, periodDays = 90 }) {
  if (!userCount || userCount < 1) return null;
  const url = `${SITE_URL}${path}`;
  return {
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    "@id": `${url}#engagement`,
    name: title,
    url,
    interactionStatistic: {
      "@type": "InteractionCounter",
      interactionType: { "@type": interactionType },
      userInteractionCount: userCount,
      description: `Estimates run on this page in the last ${periodDays} days`,
    },
  };
}

